function moneyInCents(value) {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (String(value).trim() === "") return null;
  const amount = Number(value);
  const cents = Math.round((amount + Math.sign(amount) * Number.EPSILON) * 100);
  return Number.isFinite(amount) && Number.isSafeInteger(cents) ? cents : null;
}

export function parseFoodCost(value) {
  if (String(value ?? "").trim() === "") return null;
  const text = String(value).trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text)) {
    throw new Error(
      "Enter a cost of $0 or more, with no more than two decimal places.",
    );
  }
  const cents = moneyInCents(text);
  if (cents === null) throw new Error("Enter a valid cost.");
  return cents / 100;
}

// Match costs to the original line so an edited/replaced sale cannot inherit its cost.
export function foodCostMatchesLine(record, line) {
  return Boolean(
    record &&
      line &&
      record.food === String(line.food || "").trim() &&
      record.quantity === Number(line.quantity) &&
      record.total === moneyInCents(line.total),
  );
}

export function getFoodLineCost(record, line) {
  if (!foodCostMatchesLine(record, line)) return null;
  const cents = moneyInCents(record.cost);
  return cents !== null && cents >= 0 ? cents / 100 : null;
}

export function makeFoodCostRecord(line, cost) {
  return {
    food: String(line.food || "").trim(),
    quantity: Number(line.quantity),
    total: moneyInCents(line.total),
    cost,
  };
}

export function getFoodReportNames(transactions, foodItems = []) {
  return Array.from(
    new Set(
      [
        ...foodItems.map((item) => String(item.name || "").trim()),
        ...transactions.flatMap((transaction) =>
          (Array.isArray(transaction.FoodOrder)
            ? transaction.FoodOrder
            : []
          ).map((line) => String(line?.food || "").trim()),
        ),
      ].filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));
}

export function suggestIncludedFoods(names) {
  return names.filter(
    (name) =>
      /\b(pizzas?|chicken|salads?)\b/i.test(name) &&
      !/\b(snacks?|chips?|crackers?|candy|crisps?)\b/i.test(name),
  );
}

export function makeFoodOrderCostRecord(row, cost) {
  return { itemsSignature: row.itemsSignature, cost };
}

export function buildFoodOrderRows(transactions, includedFoods = []) {
  const included = new Set(includedFoods);
  return transactions
    .flatMap((transaction) => {
      if (!Array.isArray(transaction.FoodOrder)) return [];
      const groups = new Map();
      transaction.FoodOrder.forEach((line, index) => {
        const food = String(line?.food || "").trim();
        const quantity = Number(line?.quantity);
        if (!included.has(food) || !Number.isFinite(quantity) || quantity <= 0)
          return;
        const orderId = line.orderId || "legacy";
        if (!groups.has(orderId)) groups.set(orderId, []);
        groups
          .get(orderId)
          .push({
            index,
            food,
            quantity,
            revenueCents: moneyInCents(line.total),
            line,
          });
      });
      return Array.from(groups, ([orderId, items]) => {
        const itemsSignature = JSON.stringify(
          items.map(({ index, food, quantity, revenueCents }) => ({
            index,
            food,
            quantity,
            revenueCents,
          })),
        );
        const recorded = transaction.FoodOrderProfitCosts?.[orderId];
        let cost = null;
        if (recorded) {
          const cents = moneyInCents(recorded.cost);
          if (
            recorded.itemsSignature === itemsSignature &&
            cents !== null &&
            cents >= 0
          )
            cost = cents / 100;
        } else {
          // Preserve earlier item costs only when every included item has a saved cost.
          const previousCosts = items.map(({ index, line }) =>
            getFoodLineCost(transaction.FoodOrderCosts?.[index], line),
          );
          if (previousCosts.every((value) => value !== null)) {
            cost =
              previousCosts.reduce(
                (sum, value) => sum + moneyInCents(value),
                0,
              ) / 100;
          }
        }
        const revenueCents = items.some((item) => item.revenueCents === null)
          ? null
          : items.reduce((sum, item) => sum + item.revenueCents, 0);
        const orderedAt = items[0].line.orderedAt;
        const orderDate = orderedAt ? new Date(orderedAt) : null;
        const hasOrderDate = orderDate && Number.isFinite(orderDate.getTime());
        const date = hasOrderDate
          ? orderDate.toLocaleDateString()
          : transaction.Date || "";
        const time = hasOrderDate
          ? orderDate.toLocaleTimeString()
          : transaction.Time || "";
        const timestamp = hasOrderDate
          ? orderDate.getTime()
          : Date.parse(`${date} ${time}`);
        const foodQuantities = new Map();
        for (const item of items)
          foodQuantities.set(
            item.food,
            (foodQuantities.get(item.food) || 0) + item.quantity,
          );
        return {
          id: `${transaction.id}:${orderId}`,
          transactionId: transaction.id,
          orderId,
          itemsSignature,
          date,
          time,
          timestamp: Number.isFinite(timestamp) ? timestamp : 0,
          customer: transaction.PName || "Unknown",
          food: Array.from(
            foodQuantities,
            ([food, quantity]) => `${quantity} × ${food}`,
          ).join(" + "),
          quantity: items.reduce((sum, item) => sum + item.quantity, 0),
          revenue: revenueCents === null ? null : revenueCents / 100,
          cost,
          profit:
            revenueCents === null || cost === null
              ? null
              : (revenueCents - moneyInCents(cost)) / 100,
          legacy: orderId === "legacy",
        };
      });
    })
    .sort((a, b) => b.timestamp - a.timestamp || a.id.localeCompare(b.id));
}

export function summarizeFoodOrders(rows) {
  let revenue = 0;
  let cost = 0;
  let profit = 0;
  let costedRevenue = 0;
  let missingCosts = 0;
  let missingRevenue = 0;
  let profitLines = 0;
  let quantity = 0;
  const orders = new Set();
  for (const row of rows) {
    orders.add(row.id);
    quantity += row.quantity;
    if (row.revenue === null) missingRevenue += 1;
    else revenue += moneyInCents(row.revenue);
    if (row.cost === null) missingCosts += 1;
    else cost += moneyInCents(row.cost);
    if (row.profit !== null) {
      profit += moneyInCents(row.profit);
      costedRevenue += moneyInCents(row.revenue);
      profitLines += 1;
    }
  }
  return {
    orderCount: orders.size,
    lineCount: rows.length,
    quantity,
    revenue: revenue / 100,
    cost: cost / 100,
    profit: profitLines ? profit / 100 : null,
    margin:
      costedRevenue > 0 && profitLines ? (profit / costedRevenue) * 100 : null,
    missingCosts,
    missingRevenue,
    profitLines,
  };
}

export function foodReportCSV(headers, rows) {
  const escape = (value) => {
    let text = String(value ?? "");
    // Prevent customer/item names from becoming spreadsheet formulas.
    if (typeof value === "string" && /^[\s]*[=+\-@]/.test(text))
      text = `'${text}`;
    return `"${text.replaceAll('"', '""')}"`;
  };
  return [headers, ...rows]
    .map((row) => row.map(escape).join(","))
    .join("\r\n");
}
